import { combineLatest as observableCombineLatest, concat as observableConcat, merge as observableMerge, empty as observableEmpty, of as observableOf, Subject, } from "rxjs";
import { auditTime, catchError, debounceTime, distinctUntilChanged, filter, map, publish, publishReplay, refCount, retry, share, skip, startWith, switchMap, take, takeUntil, withLatestFrom, } from "rxjs/operators";
import { GraphMode } from "../../graph/GraphMode";
import { State } from "../../state/State";
import { SequenceDOMRenderer } from "./SequenceDOMRenderer";
import { EdgeDirection } from "../../graph/edge/EdgeDirection";
import { Component } from "../Component";
/**
 * @class SequenceComponent
 * @classdesc Component showing navigation arrows for sequence directions
 * as well as playing button. Exposes an API to start and stop play.
 */
export class SequenceComponent extends Component {
    constructor(name, container, navigator, renderer, scheduler) {
        super(name, container, navigator);
        this._sequenceDOMRenderer = renderer ? renderer : new SequenceDOMRenderer(container);
        this._scheduler = scheduler;
        this._containerWidth$ = new Subject();
        this._hoveredKeySubject$ = new Subject();
        this._hoveredKey$ = this._hoveredKeySubject$.pipe(share());
        this._navigator.playService.playing$
            .pipe(skip(1), withLatestFrom(this._configuration$))
            .subscribe(([playing, configuration]) => {
            this.fire(SequenceComponent.playingchanged, undefined, playing);
            if (playing === configuration.playing) {
                return;
            }
            if (playing) {
                this.play();
            }
            else {
                this.stop();
            }
        });
        this._navigator.playService.direction$
            .pipe(skip(1), withLatestFrom(this._configuration$))
            .subscribe(([direction, configuration]) => {
            if (direction !== configuration.direction) {
                this.setDirection(direction);
            }
        });
    }
    /**
     * Get hovered key observable.
     *
     * @description An observable emitting the key of the node for the direction
     * arrow that is being hovered. When the mouse leaves a direction arrow null
     * is emitted.
     *
     * @returns {Observable<string>}
     */
    get hoveredKey$() {
        return this._hoveredKey$;
    }
    /**
     * Start playing.
     *
     * @fires PlayerComponent#playingchanged
     */
    play() {
        this.configure({ playing: true });
    }
    /**
     * Stop playing.
     *
     * @fires PlayerComponent#playingchanged
     */
    stop() {
        this.configure({ playing: false });
    }
    /**
     * Set the direction to follow when playing.
     *
     * @param {EdgeDirection} direction - The direction that will be followed when playing.
     */
    setDirection(direction) {
        this.configure({ direction: direction });
    }
    /**
     * Set highlight key.
     *
     * @description The arrow pointing towards the node corresponding to the
     * highlight key will be highlighted.
     *
     * @param {string} highlightKey Key of node to be highlighted if existing.
     */
    setHighlightKey(highlightKey) {
        this.configure({ highlightKey: highlightKey });
    }
    /**
     * Set max width of container element.
     *
     * @description Set max width of the container element holding
     * the sequence navigation elements. If the min width is larger than the
     * max width the min width value will be used.
     *
     * The container element is automatically resized when the resize
     * method on the Viewer class is called.
     *
     * @param {number} minWidth
     */
    setMaxWidth(maxWidth) {
        this.configure({ maxWidth: maxWidth });
    }
    /**
     * Set min width of container element.
     *
     * @description Set min width of the container element holding
     * the sequence navigation elements. If the min width is larger than the
     * max width the min width value will be used.
     *
     * The container element is automatically resized when the resize
     * method on the Viewer class is called.
     *
     * @param {number} minWidth
     */
    setMinWidth(minWidth) {
        this.configure({ minWidth: minWidth });
    }
    /**
     * Set the value indicating whether the sequence UI elements should be visible.
     *
     * @param {boolean} visible
     */
    setVisible(visible) {
        this.configure({ visible: visible });
    }
    _activate() {
        this._sequenceDOMRenderer.activate();
        const edgeStatus$ = this._navigator.stateService.currentNode$.pipe(switchMap((node) => {
            return node.sequenceEdges$;
        }), publishReplay(1), refCount());
        const sequence$ = this._navigator.stateService.currentNode$.pipe(distinctUntilChanged(undefined, (node) => {
            return node.sequenceKey;
        }), switchMap((node) => {
            return observableConcat(observableOf(null), this._navigator.graphService.cacheSequence$(node.sequenceKey).pipe(retry(3), catchError((e) => {
                console.error("Failed to cache sequence", e);
                return observableOf(null);
            })));
        }), startWith(null), publishReplay(1), refCount());
        this._sequenceSubscription = sequence$.subscribe();
        const rendererKey$ = this._sequenceDOMRenderer.index$.pipe(withLatestFrom(sequence$), map(([index, sequence]) => {
            return sequence != null ? sequence.keys[index] : null;
        }), filter((key) => {
            return !!key;
        }), distinctUntilChanged(), publish(), refCount());
        this._moveSubscription = observableMerge(rendererKey$.pipe(debounceTime(100, this._scheduler)), rendererKey$.pipe(auditTime(400, this._scheduler)))
            .pipe(distinctUntilChanged(), switchMap((key) => {
            return this._navigator.moveToKey$(key).pipe(catchError(() => {
                return observableEmpty();
            }));
        }))
            .subscribe();
        this._setSequenceGraphModeSubscription = this._sequenceDOMRenderer.changingPositionChanged$
            .pipe(filter((changing) => {
            return changing;
        }))
            .subscribe(() => {
            this._navigator.graphService.setGraphMode(GraphMode.Sequence);
        });
        this._setSpatialGraphModeSubscription = this._sequenceDOMRenderer.changingPositionChanged$
            .pipe(filter((changing) => {
            return !changing;
        }))
            .subscribe(() => {
            this._navigator.graphService.setGraphMode(GraphMode.Spatial);
        });
        this._navigator.graphService.graphMode$
            .pipe(switchMap((mode) => {
            return mode === GraphMode.Spatial
                ? this._navigator.stateService.currentNode$.pipe(take(2))
                : observableEmpty();
        }), filter((node) => {
            return !node.spatialEdges.cached;
        }), switchMap((node) => {
            return this._navigator.graphService.cacheNode$(node.key).pipe(catchError(() => {
                return observableEmpty();
            }));
        }))
            .subscribe();
        this._stopSubscription = this._sequenceDOMRenderer.changingPositionChanged$
            .pipe(filter((changing) => {
            return changing;
        }))
            .subscribe(() => {
            this._navigator.playService.stop();
        });
        this._cacheSequenceNodesSubscription = observableCombineLatest(this._navigator.graphService.graphMode$, this._sequenceDOMRenderer.changingPositionChanged$.pipe(startWith(false), distinctUntilChanged()))
            .pipe(withLatestFrom(this._navigator.stateService.currentNode$), switchMap(([[mode, changing], node]) => {
            return changing && mode === GraphMode.Sequence
                ? this._navigator.graphService.cacheSequenceNodes$(node.sequenceKey, node.key).pipe(retry(3), catchError((error) => {
                    console.error("Failed to cache sequence nodes.", error);
                    return observableEmpty();
                }))
                : observableEmpty();
        }))
            .subscribe();
        const position$ = sequence$.pipe(switchMap((sequence) => {
            if (!sequence) {
                return observableOf({ index: null, max: null });
            }
            let firstCurrentKey = true;
            return this._sequenceDOMRenderer.changingPositionChanged$.pipe(startWith(false), distinctUntilChanged(), switchMap((changingPosition) => {
                const skipCount = !changingPosition && firstCurrentKey ? 0 : 1;
                firstCurrentKey = false;
                return changingPosition
                    ? rendererKey$
                    : this._navigator.stateService.currentNode$.pipe(map((node) => {
                        return node.key;
                    }), distinctUntilChanged(), skip(skipCount));
            }), map((key) => {
                const index = sequence.keys.indexOf(key);
                if (index === -1) {
                    return { index: null, max: null };
                }
                return { index: index, max: sequence.keys.length - 1 };
            }));
        }));
        const earth$ = this._navigator.stateService.state$.pipe(map((state) => {
            return state === State.Earth;
        }), distinctUntilChanged());
        this._renderSubscription = observableCombineLatest(edgeStatus$, this._configuration$, this._containerWidth$, this._sequenceDOMRenderer.changed$.pipe(startWith(this._sequenceDOMRenderer)), this._navigator.playService.speed$, position$, earth$, this._navigator.playService.bridge$)
            .pipe(map(([edgeStatus, configuration, containerWidth, , speed, position, earth, bridge]) => {
            const vNode = this._sequenceDOMRenderer.render(edgeStatus, configuration, containerWidth, speed, position.index, position.max, !earth, this, this._navigator, bridge);
            return { name: this._name, vnode: vNode };
        }))
            .subscribe(this._container.domRenderer.render$);
        this._setSpeedSubscription = this._sequenceDOMRenderer.speed$.subscribe((speed) => {
            this._navigator.playService.setSpeed(speed);
        });
        this._setDirectionSubscription = this._configuration$
            .pipe(map((configuration) => {
            return configuration.direction;
        }), distinctUntilChanged())
            .subscribe((direction) => {
            this._navigator.playService.setDirection(direction);
        });
        this._containerWidthSubscription = observableCombineLatest(this._container.renderService.size$, this._configuration$.pipe(distinctUntilChanged((value1, value2) => {
            return value1[0] === value2[0] && value1[1] === value2[1];
        }, (configuration) => {
            return [configuration.minWidth, configuration.maxWidth];
        })))
            .pipe(map(([size, configuration]) => {
            return this._sequenceDOMRenderer.getContainerWidth(size, configuration);
        }))
            .subscribe(this._containerWidth$);
        this._playingSubscription = this._configuration$
            .pipe(map((configuration) => {
            return configuration.playing;
        }), distinctUntilChanged())
            .subscribe((playing) => {
            if (playing) {
                this._navigator.playService.play();
            }
            else {
                this._navigator.playService.stop();
            }
        });
        this._hoveredKeySubscription = this._sequenceDOMRenderer.mouseEnterDirection$
            .pipe(switchMap((direction) => {
            const edgeTo$ = edgeStatus$.pipe(map((edgeStatus) => {
                for (const edge of edgeStatus.edges) {
                    if (edge.data.direction === direction) {
                        return edge.to;
                    }
                }
                return null;
            }), takeUntil(this._sequenceDOMRenderer.mouseLeaveDirection$));
            return observableConcat(edgeTo$, observableOf(null));
        }), distinctUntilChanged())
            .subscribe(this._hoveredKeySubject$);
        this._emitHoveredKeySubscription = this._hoveredKey$.subscribe((key) => {
            this.fire(SequenceComponent.hoveredkeychanged, undefined, key);
        });
        this._timelinetoggleSubscription = this._navigator.playService.timelinetoggle$.subscribe((toggle) => {
            this.configure({
                showtimeline: !toggle,
                height: toggle ? 47 : 36,
            });
        });
    }
    _deactivate() {
        this._emitHoveredKeySubscription.unsubscribe();
        this._renderSubscription.unsubscribe();
        this._playingSubscription.unsubscribe();
        this._containerWidthSubscription.unsubscribe();
        this._hoveredKeySubscription.unsubscribe();
        this._setSpeedSubscription.unsubscribe();
        this._setDirectionSubscription.unsubscribe();
        this._setSequenceGraphModeSubscription.unsubscribe();
        this._setSpatialGraphModeSubscription.unsubscribe();
        this._sequenceSubscription.unsubscribe();
        this._moveSubscription.unsubscribe();
        this._cacheSequenceNodesSubscription.unsubscribe();
        this._stopSubscription.unsubscribe();
        this._timelinetoggleSubscription.unsubscribe();
        this._sequenceDOMRenderer.deactivate();
    }
    _getDefaultConfiguration() {
        return {
            direction: EdgeDirection.Next,
            maxWidth: 108,
            minWidth: 70,
            playing: false,
            visible: true,
            showtimeline: true,
            height: 36,
            locale: {
                backward: "Backward",
                forward: "Forward",
                direction: "Direction",
                speed: "Speed",
                play: "Play/Pause",
            },
        };
    }
}
/** @inheritdoc */
SequenceComponent.componentName = "sequence";
/**
 * Event fired when playing starts or stops.
 *
 * @event SequenceComponent#playingchanged
 * @type {boolean} Indicates whether the player is playing.
 */
SequenceComponent.playingchanged = "playingchanged";
/**
 * Event fired when the hovered key changes.
 *
 * @description Emits the key of the node for the direction
 * arrow that is being hovered. When the mouse leaves a
 * direction arrow null is emitted.
 *
 * @event SequenceComponent#hoveredkeychanged
 * @type {string} The hovered key, null if no key is hovered.
 */
SequenceComponent.hoveredkeychanged = "hoveredkeychanged";
//# sourceMappingURL=SequenceComponent.js.map